import { useCallback, useEffect, useRef, useMemo, useState } from "react";
import { useQuery } from "@apollo/client";
import usePagination from "@/hooks/usePagination";
import { useParams } from "@/hooks/useSearchParams";
import { PEOPLE_QUERY, PeopleQueryInput, PeopleQueryResponse, PersonOrderByInput } from "../queries";
import { ColumnSort } from "@tanstack/react-table";
import { snakeCase } from "lodash";

export default function usePeopleQuery(quickSearch?: PeopleQueryInput, pgaIds?: string[]) {
  const { paginationVariables, createHeaderPagination, createFooterPagination, resetPagination } = usePagination();
  const { params, addSearchParam, deleteSearchParams, searchParamsUrl } = useParams();
  const [debouncedSearch, setDebouncedSearch] = useState(params.search ?? "");
  const [searchClearSignal, setSearchClearSignal] = useState(0);

  const [orderBy, setOrderBy] = useState<ColumnSort[]>([]);

  useEffect(() => {
    setDebouncedSearch(params.search ?? "");
  }, [params.search]);

  const memberClassifications = useMemo(
    () => quickSearch?.memberClassifications || params.memberClassifications || [],
    [quickSearch?.memberClassifications, params.memberClassifications]
  );
  const programTypes = useMemo(
    () => quickSearch?.programTypes || params.programTypes || [],
    [quickSearch?.programTypes, params.programTypes]
  );
  const statuses = useMemo(
    () => quickSearch?.statuses || params.statuses || [],
    [quickSearch?.statuses, params.statuses]
  );
  const sectionIds = useMemo(
    () => quickSearch?.sectionIds || params.sectionIds || [],
    [quickSearch?.sectionIds, params.sectionIds]
  );

  const otherQuickSearchParams = useMemo(() => {
    if (!quickSearch) return {};
    const { sectionIds, memberClassifications, programTypes, statuses, ...rest } = quickSearch;
    return rest;
  }, [quickSearch]);

  const prevDebouncedSearchRef = useRef(debouncedSearch);
  const prevMemberClassificationsRef = useRef(memberClassifications);
  const prevProgramTypesRef = useRef(programTypes);
  const prevStatusesRef = useRef(statuses);
  const prevSectionIdsRef = useRef(sectionIds);
  const prevQuickSearchRef = useRef(quickSearch);

  const debouncedSearchChanged = prevDebouncedSearchRef.current !== debouncedSearch;
  const memberClassificationsChanged =
    JSON.stringify(prevMemberClassificationsRef.current) !== JSON.stringify(memberClassifications);
  const programTypesChanged = JSON.stringify(prevProgramTypesRef.current) !== JSON.stringify(programTypes);
  const statusesChanged = JSON.stringify(prevStatusesRef.current) !== JSON.stringify(statuses);
  const sectionIdsChanged = JSON.stringify(prevSectionIdsRef.current) !== JSON.stringify(sectionIds);
  const quickSearchChanged = JSON.stringify(prevQuickSearchRef.current) !== JSON.stringify(quickSearch);

  useEffect(() => {
    prevDebouncedSearchRef.current = debouncedSearch;
    prevMemberClassificationsRef.current = memberClassifications;
    prevProgramTypesRef.current = programTypes;
    prevStatusesRef.current = statuses;
    prevSectionIdsRef.current = sectionIds;
    prevQuickSearchRef.current = quickSearch;
  }, [debouncedSearch, memberClassifications, programTypes, statuses, sectionIds, quickSearch]);

  useEffect(() => {
    if (
      debouncedSearchChanged ||
      memberClassificationsChanged ||
      programTypesChanged ||
      statusesChanged ||
      sectionIdsChanged ||
      quickSearchChanged
    ) {
      resetPagination();
    }
  }, [
    debouncedSearchChanged,
    memberClassificationsChanged,
    programTypesChanged,
    statusesChanged,
    sectionIdsChanged,
    quickSearchChanged,
    resetPagination
  ]);

  const onDebouncedSearchChange = useCallback((value: string) => {
    setDebouncedSearch(value);
  }, []);

  const clearSearch = useCallback(() => {
    setDebouncedSearch("");
    deleteSearchParams("search");
    setSearchClearSignal((signal) => signal + 1);
  }, [deleteSearchParams]);

  const { id, desc } = orderBy[0] || {};
  const orderByVariable: PersonOrderByInput | undefined = id
    ? {
        field: snakeCase(id).toUpperCase() as PersonOrderByInput["field"],
        direction: desc ? "DESC" : "ASC"
      }
    : undefined;

  const queryVariables = {
    ...paginationVariables,
    input: {
      ...otherQuickSearchParams,
      search: debouncedSearch ?? "",
      memberClassifications: memberClassifications.length > 0 ? memberClassifications : undefined,
      programTypes: programTypes.length > 0 ? programTypes : undefined,
      statuses: statuses.length > 0 ? statuses : undefined,
      sectionIds: sectionIds.length > 0 ? sectionIds : undefined,
      pgaIds: pgaIds && pgaIds.length > 0 ? pgaIds : undefined
    },
    orderBy: orderByVariable
  };

  const { data, loading } = useQuery(PEOPLE_QUERY, {
    variables: queryVariables,
    errorPolicy: "all"
  });

  const formattedData: PeopleQueryResponse | undefined = data
    ? {
        people: data.people || { nodes: [], pageInfo: { hasNextPage: false, hasPreviousPage: false }, totalCount: 0 }
      }
    : undefined;

  const handleSort = (updaterOrValue: ColumnSort[] | ((old: ColumnSort[]) => ColumnSort[])) => {
    const sortState = typeof updaterOrValue === "function" ? updaterOrValue([]) : updaterOrValue;
    const { id, desc } = sortState[0] || {};
    setOrderBy([{ id, desc }]);
  };

  return {
    data: formattedData,
    loading,
    peopleQueryVariables: queryVariables,
    createHeaderPagination,
    createFooterPagination,
    resetPagination,
    debouncedSearch,
    onDebouncedSearchChange,
    searchClearSignal,
    clearSearch,
    addSearchParam,
    deleteSearchParams,
    searchParamsUrl,
    orderBy,
    handleSort
  };
}
